import {
	type BulkAclEntity,
	AclEntityMinInfoEntityTypeEnum,
	GroupUniverseControllerApiFactory,
	IntegrationUniverseControllerApiFactory,
} from "@eu.mdotm.api/sphere";
import type { RichAccessControl } from "@eu.mdotm.api/smart-workspaces";
import { getApiGen } from "$root/api/factory";
import { repositoryHelperFactory } from "$root/api/repository";
import { type EventPayloadMap, eventBus } from "$root/event-bus";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { downloadContentDisposition, seconds } from "$root/utils";
import type { Abortable } from "@mdotm/utils/promise";
import debounce from "debounce";
// universe list is a common pool between these two repositories
import { universeRepository } from "./repository";

const { newQuery, newAction, invalidateEntity } = repositoryHelperFactory("universeWithGroups");

const universeApi = () => getApiGen(GroupUniverseControllerApiFactory);
const integrationApi = () => getApiGen(IntegrationUniverseControllerApiFactory);

export const universeWithGroupsRepository = {
	summary: newQuery("summary", (uuid: string) => axiosExtract(universeApi().getUniverseDetails(uuid))),

	universeWithGroup: newQuery("universeWithGroup", (uuid: string) => axiosExtract(universeApi().getUniverse1(uuid))),

	getACL: newQuery("acl", (uuid: string) => axiosExtract(universeApi().getAclEditStarter4(uuid))),

	updateACL: newAction(async (uuid: string, acl: Array<RichAccessControl>) => {
		await universeApi().setACL4(uuid, acl);
		universeRepository.invalidateLists();
		invalidateEntity(uuid);
	}),

	rename: newAction(async (uuid: string, name: string) => {
		await axiosExtract(universeApi().renameUniverse1(uuid, name));
		universeRepository.invalidateLists();
		invalidateEntity(uuid);
	}),

	duplicate: newAction(async (uuid: string, name: string) => {
		const clone = await axiosExtract(universeApi().duplicateUniverse1(uuid, name));
		universeRepository.invalidateLists();
		return clone;
	}),

	delete: newAction(async (uuid: string) => {
		await axiosExtract(universeApi().deleteUniverse1(uuid));
		universeRepository.invalidateLists();
	}),

	isUniverseNameAvailable: newAction((uuid: string, opts?: Abortable) => {
		return axiosExtract(universeApi().isUniverseNameAvailable1(uuid, opts));
	}),

	downloadUniverse: newAction(async (uuid: string) => {
		const response = await integrationApi().exportUniverse(uuid, {
			responseType: "blob",
		});

		return downloadContentDisposition(response);
	}),

	universeWithGroupBulkAclPreview: newQuery("universeBulkAclPreview", (acl: Array<BulkAclEntity>) =>
		axiosExtract(universeApi().getBulkACLCheck4(acl)),
	),
} as const;

eventBus.addListener(
	"investment-update",
	debounce((e: EventPayloadMap["investment-update"]) => {
		if (e.type === "UNIVERSE_UPDATE") {
			invalidateEntity(e.uuid ?? "");
		}
	}, seconds(2)),
);

eventBus.addListener(
	"shared-entity",
	debounce((e: EventPayloadMap["shared-entity"]) => {
		if (e.sharedEntityType === AclEntityMinInfoEntityTypeEnum.GroupUniverse) {
			universeRepository.invalidateLists();
			invalidateEntity(e.sharedEntityUuid ?? "");
		}
	}, seconds(2)),
);
